import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, Heading2, List, ListOrdered, Link2, AlignLeft, AlignCenter, AlignRight } from "lucide-react";

// Lightweight rich-text editor for confirmation emails. Outputs a small, email-safe HTML
// subset (the backend sanitizes again with bleach on save). No external dependency.
export function RichTextEditor({ value, onChange, placeholder }) {
  const ref = useRef(null);

  // Set initial HTML once (and when an externally-different value arrives), preserving the caret otherwise.
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => onChange(ref.current?.innerHTML || "");
  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };
  const heading = () => exec("formatBlock", "H2");
  const paragraph = () => exec("formatBlock", "P");
  const link = () => {
    const url = window.prompt("Enter the link URL (https://…)");
    if (!url) return;
    const safe = /^(https?:|mailto:|tel:)/i.test(url) ? url : `https://${url}`;
    exec("createLink", safe);
  };

  const Btn = ({ onClick, title, testid, children }) => (
    <button type="button" title={title} data-testid={testid} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="w-8 h-8 rounded-md flex items-center justify-center text-[#2A2626] hover:bg-[#F0EBE1] transition-colors">
      {children}
    </button>
  );

  return (
    <div className="rounded-lg border border-[#E5E2DC] overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-[#E5E2DC] bg-[#FAF9F6]">
        <Btn onClick={() => exec("bold")} title="Bold" testid="rte-bold"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("italic")} title="Italic" testid="rte-italic"><Italic className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("underline")} title="Underline" testid="rte-underline"><Underline className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-[#E5E2DC] mx-1" />
        <Btn onClick={heading} title="Heading" testid="rte-heading"><Heading2 className="w-4 h-4" /></Btn>
        <Btn onClick={paragraph} title="Normal text" testid="rte-paragraph"><span className="text-[11px] font-semibold">P</span></Btn>
        <span className="w-px h-5 bg-[#E5E2DC] mx-1" />
        <Btn onClick={() => exec("insertUnorderedList")} title="Bulleted list" testid="rte-ul"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Numbered list" testid="rte-ol"><ListOrdered className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-[#E5E2DC] mx-1" />
        <Btn onClick={() => exec("justifyLeft")} title="Align left" testid="rte-align-left"><AlignLeft className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("justifyCenter")} title="Align center" testid="rte-align-center"><AlignCenter className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("justifyRight")} title="Align right" testid="rte-align-right"><AlignRight className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-[#E5E2DC] mx-1" />
        <Btn onClick={link} title="Insert link" testid="rte-link"><Link2 className="w-4 h-4" /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder || "Write your confirmation message…"}
        data-testid="rte-content"
        className="min-h-[140px] px-3 py-2 text-sm text-[#2A2626] leading-relaxed focus:outline-none rte-content"
      />
      <style>{`
        .rte-content:empty:before { content: attr(data-placeholder); color: #8A857F; }
        .rte-content a { color: #2563eb; text-decoration: underline; }
        .rte-content h2 { font-size: 18px; font-weight: 700; margin: 8px 0; }
        .rte-content ul { list-style: disc; padding-left: 22px; }
        .rte-content ol { list-style: decimal; padding-left: 22px; }
        .rte-content p { margin: 6px 0; }
      `}</style>
    </div>
  );
}
